import { useEffect, useRef, useState } from 'react';
import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { carShareUrl, shareCar } from '../../application/pwa/shareCar';
import { shareLink } from '../../application/pwa/shareImage';
import { carName, carTagline } from '../../domain/cars/carCatalogue';
import { carShareMessage } from '../../domain/cars/carShare';
import { LinkIcon, PictureIcon, PostIcon, ShareIcon } from '../icons/ControlIcons';

/**
 * How this car travels (FR-09) — the action, the chooser and what happened.
 *
 * TWO DESTINATIONS, and the choice is real rather than a setting. A picture is
 * the only thing that works in the places people actually post a car: Instagram
 * takes no links at all, and Messages renders one as a grey rectangle. An
 * address is worth more than a picture everywhere that unfurls one, and it is
 * the only share that opens the app on the other side. Guessing between them on
 * the reader's behalf is what a single share button would be doing.
 *
 * The two rows say where the share GOES rather than what the button does, which
 * is why the copy is "As a picture" and not "Share image".
 *
 * A real `<dialog>` opened with `showModal()`: focus trapping, Escape, the top
 * layer and an inert background are four things the platform gets right.
 *
 * `onChoose` is async and the row closes the sheet only AFTER it settles, so the
 * OS share sheet opens over this one rather than under a dialog that is already
 * tearing down — which on iOS is the difference between a share sheet and
 * nothing happening at all.
 */

/**
 * What the reader is told afterwards, and what they are deliberately not.
 *
 * A share that WORKED needs no confirmation: the OS sheet they just used is the
 * confirmation, and a toast after it is the app congratulating itself. A share
 * they CANCELLED needs none either — they know. The two worth reporting are the
 * two where something happened that the reader did not see: the link went to the
 * clipboard, or nothing went anywhere.
 */
const RESULT_MESSAGE_KEYS = {
    copied: 'cars.share.copied',
    unavailable: 'cars.share.failed',
};

function ShareChoice({ Glyph, label, note, onChoose }) {
    return (
        <button type="button" className="share-choice" onClick={onChoose}>
            <span className="share-choice__glyph" aria-hidden="true"><Glyph size={24} /></span>
            <span className="share-choice__text">
                <span className="share-choice__label">{label}</span>
                <span className="share-choice__note">{note}</span>
            </span>
        </button>
    );
}

function ShareChooser({ car, compose, onResult, onClose }) {
    const t = useTranslate();
    const { localeCode, languageCode } = useLocale();
    const dialogRef = useRef(null);

    // Opening is a one-off, and deliberately its own effect: an effect that
    // depended on `onClose` would close and re-open the dialog on every parent
    // render, and the second `showModal()` throws.
    useEffect(() => {
        const dialog = dialogRef.current;

        dialog.showModal();

        return () => {
            if (dialog.open) {
                dialog.close();
            }
        };
    }, []);

    useEffect(() => {
        const dialog = dialogRef.current;

        dialog.addEventListener('close', onClose);

        return () => dialog.removeEventListener('close', onClose);
    }, [onClose]);

    const name = carName(car);
    const { title, text } = compose({ name, tagline: carTagline(car) }, t);
    const payload = { carId: car.id, name, languageCode, localeCode, title, text };

    const choose = (share) => async () => {
        onResult(await share());
        dialogRef.current?.close();
    };

    return (
        <dialog
            className="share-sheet"
            ref={dialogRef}
            aria-labelledby="share-sheet-title"
            // A click that lands on the dialog element ITSELF landed on the
            // backdrop: everything inside is a child of the panel.
            onClick={(event) => {
                if (event.target === dialogRef.current) {
                    dialogRef.current.close();
                }
            }}
        >
            <span className="share-sheet__grip" aria-hidden="true" />
            <h2 className="share-sheet__title" id="share-sheet-title">{t('cars.share.choose')}</h2>

            <div className="share-sheet__choices">
                <ShareChoice
                    Glyph={PictureIcon}
                    label={t('cars.share.asCard')}
                    note={t('cars.share.asCardNote')}
                    onChoose={choose(() => shareCar(payload))}
                />
                {/* The same card in a different rectangle, and a real
                    distinction rather than a setting. 4:5 is what a story and a
                    message bubble render whole; a Facebook feed letterboxes that
                    down to a column, and 1.91:1 is the widest it renders before
                    it crops. A chooser that offered a SHAPE picker instead would
                    be asking the reader to know that — so the row names the
                    destination and the shape is this file's problem.

                    It is also a different composition, not just a crop: the
                    1200x630 unfurl card is fetched by a crawler that prints
                    `og:description` as text beside it, so it deliberately says
                    less. A post is uploaded and carries only whatever caption
                    the poster types, so the wide card is the one that puts the
                    tagline and the specs inside the picture. */}
                <ShareChoice
                    Glyph={PostIcon}
                    label={t('cars.share.asPost')}
                    note={t('cars.share.asPostNote')}
                    onChoose={choose(() => shareCar({ ...payload, shape: 'landscape' }))}
                />
                <ShareChoice
                    Glyph={LinkIcon}
                    label={t('cars.share.asLink')}
                    note={t('cars.share.asLinkNote')}
                    onChoose={choose(() => shareLink({
                        title,
                        text,
                        // The canonical address, not `window.location.href` — the
                        // current one carries the sender's filter query, which is
                        // their shelf rather than anything about this car.
                        url: carShareUrl(localeCode, car.id, typeof window === 'undefined' ? null : window.location),
                    }))}
                />
            </div>
        </dialog>
    );
}

/**
 * `compose` and `actionKey` are the sheet's two seams, and they exist because a
 * won car travels through the same three destinations as any other one
 * (SPEC-010 PR-10.7). What changes between the catalogue's share and the
 * ceremony's is the SENTENCE — "one of the machines that made the golden era"
 * against "I won this" — and the label on the button that opens the chooser.
 * A second sheet beside this one would be the same dialog, the same ladder and
 * the same three rows with two strings different, which is exactly the "second
 * way of doing an established thing" the repo contract forbids.
 */
export default function CarShareSheet({
    car,
    compose = carShareMessage,
    actionKey = 'cars.share.action',
}) {
    const t = useTranslate();
    const [isOpen, setOpen] = useState(false);
    const [result, setResult] = useState(null);
    const resultKey = RESULT_MESSAGE_KEYS[result] ?? null;

    return (
        <div className="car-share">
            <button type="button" className="button button--primary car-share__action" onClick={() => setOpen(true)}>
                <ShareIcon size={20} />
                {t(actionKey)}
            </button>

            {/* Always in the document, empty until there is something to say. A
                live region inserted at the moment it gains text is a live region
                several screen readers never announce. */}
            <p className="car-share__result" role="status">
                {resultKey ? t(resultKey) : ''}
            </p>

            {isOpen ? (
                <ShareChooser
                    car={car}
                    compose={compose}
                    onResult={setResult}
                    onClose={() => setOpen(false)}
                />
            ) : null}
        </div>
    );
}
